import { IColumn } from "@fluentui/react";
import React, { ReactElement } from "react";
import {
    createListTableColumns,
    ensure,
    Link,
    ListTableColumn,
    renderEnumValue,
    renderOptionalName,
    Text
} from "sprint-common-web";

import { LanguageDetailsInfo } from "../../../common/components/details-info/LanguageDetailsInfo";
import { VideoDetailsInfo } from "../../../common/components/details-info/VideoDetailsInfo";
import { AssetStatus } from "../../../common/types/AssetStatus";
import { DynamicSubtitleType } from "../../../common/types/DynamicSubtitleType";
import { RecordType } from "../../../common/types/RecordType";
import { renderAssetInfo } from "../../../common/utils/renderAssetInfo";
import { renderContent } from "../../file-records/common/components/renderContent";
import { LookupCloudAssetItem } from "./LookupCloudAssetItem";

interface CreateLookupCloudAssetsListTableColumnsProps {
    onClick: (item: LookupCloudAssetItem) => void;
}

export const createLookupCloudAssetsListTableColumns = (
    props: CreateLookupCloudAssetsListTableColumnsProps
): ListTableColumn<LookupCloudAssetItem>[] => {
    return createListTableColumns<LookupCloudAssetItem>([
        {
            type: "interactive",
            key: "filePath",
            name: "File path",
            minWidth: 200,
            maxWidth: 280,
            isSortable: false,
            onItemInvoked: (item) => props.onClick(item),
            onRender: renderFilePath
        },
        {
            type: "basic",
            key: "content",
            name: "Content",
            minWidth: 65,
            maxWidth: 350,
            isSortable: true,
            onRender: renderCloudAssetContent
        },
        {
            type: "basic",
            key: "recordType",
            name: "Record type",
            minWidth: 100,
            maxWidth: 100,
            isSortable: false,
            onRender: (item) => renderAssetInfo({ recordType: item?.recordType, isTrimmed: !!item?.trimming })
        },
        {
            type: "basic",
            key: "originator",
            name: "Originator",
            minWidth: 90,
            maxWidth: 120,
            isSortable: false,
            onRender: renderOptionalName
        },
        {
            type: "basic",
            key: "soundtrack",
            name: "Details",
            minWidth: 120,
            maxWidth: 160,
            isSortable: false,
            onRender: renderAssetDetails
        },
        {
            type: "basic",
            key: "status",
            name: "Status",
            minWidth: 70,
            maxWidth: 70,
            isSortable: false,
            onRender: renderStatus
        }
    ]);
};

const renderAssetDetails = (item?: LookupCloudAssetItem): ReactElement | null => {
    ensure(item);

    switch (item.recordType) {
        case RecordType.Video:
            return <VideoDetailsInfo key="video" subtitle={item.subtitle} aspectRatio={item.aspectRatio} />;
        case RecordType.Audio:
            return <LanguageDetailsInfo key="audio" language={item.soundtrack} />;
        case RecordType.Subtitle:
            return (
                <LanguageDetailsInfo
                    key="subtitle"
                    language={item.subtitle}
                    subtitleType={
                        item.subtitle?.dynamicSubtitleType
                            ? DynamicSubtitleType[item.subtitle?.dynamicSubtitleType]
                            : undefined
                    }
                />
            );
        default:
            return <></>;
    }
};

const renderFilePath = (
    item?: LookupCloudAssetItem,
    _index?: number,
    column?: ListTableColumn<unknown> & IColumn
): ReactElement | null => {
    if (!item || !column) return null;

    const onClick = () => column.onItemInvoked && column.onItemInvoked(item);

    return (
        <Link
            onClick={onClick}
            styles={{
                root: { width: "100%" }
            }}
        >
            <Text onClick={onClick} value={item.filePath} variant="smallPlus" tooltip={item.filePath} />
        </Link>
    );
};

const renderCloudAssetContent = (item?: LookupCloudAssetItem): ReactElement | null => {
    ensure(item);

    return renderContent({
        content: item.contentTitle,
        contentType: item.contentType,
        versions: item.versions
    });
};

const renderStatus = (
    item?: LookupCloudAssetItem,
    _index?: number,
    column?: ListTableColumn<unknown> & IColumn
): ReactElement | null => {
    ensure(item);

    return <Text variant={"small"}>{renderEnumValue(AssetStatus, item, _index, column)}</Text>;
};
